import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './context/AuthContext'
import { SettingsProvider } from './context/SettingsContext'
import { useDevice } from './hooks/useDevice'
import { AnimatePresence, motion } from 'framer-motion'
import Layout from './components/Layout'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Operations from './pages/Operations'
import Tasks from './pages/Tasks'
import Settings from './pages/Settings'
import Partners from './pages/Partners'
import Projects from './pages/Projects'
import Invoices from './pages/Invoices'
import Products from './pages/Products'
import Inventory from './pages/Inventory'
import Logs from './pages/Logs'
import Clients from './pages/Clients'
import Calculator from './pages/Calculator'
import CRM from './pages/CRM'
import Bots from './pages/Bots'
import Referrals from './pages/Referrals'
import Analytics from './pages/Analytics'
import Members from './pages/Members'
import AISettings from './pages/AISettings'
import Events from './pages/Events'
import Profile from './pages/Profile'

// Helper to handle lazy loading errors (e.g. after a new deployment)
const lazyWithRetry = (componentImport) =>
    React.lazy(async () => {
        try {
            return await componentImport();
        } catch (error) {
            console.error("Chunk load error, reloading...", error);
            window.location.reload();
            return { default: () => null };
        }
    });

const Checkin = lazyWithRetry(() => import('./pages/Checkin'));

const queryClient = new QueryClient()

const ProtectedRoute = ({ component, allowedRoles }) => {
    const { user, loading } = useAuth();
    if (loading) return (
        <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
    );
    if (!user) return <Navigate to="/login" />;
    if (allowedRoles && !allowedRoles.includes(user.role) && user.role !== 'superadmin') {
        return <Navigate to="/" />;
    }
    return <Layout>{component}</Layout>;
};

function AnimatedRoutes() {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="w-full"
            >
                <Routes location={location}>
                    <Route path="/login" element={<div className="min-h-screen bg-neutral-950 flex items-center justify-center p-4"><Login /></div>} />
                    
                    <Route path="/" element={<ProtectedRoute component={<Dashboard />} />} />
                    <Route path="/tasks" element={<ProtectedRoute component={<Tasks />} />} />
                    <Route path="/operations" element={<ProtectedRoute component={<Operations />} allowedRoles={['admin', 'superadmin', 'partner']} />} />
                    <Route path="/invoices" element={<ProtectedRoute component={<Invoices />} allowedRoles={['admin', 'superadmin', 'partner']} />} />
                    <Route path="/partners" element={<ProtectedRoute component={<Partners />} allowedRoles={['admin', 'superadmin', 'partner']} />} />
                    <Route path="/projects" element={<ProtectedRoute component={<Projects />} />} />
                    <Route path="/products" element={<ProtectedRoute component={<Products />} allowedRoles={['admin', 'superadmin', 'partner']} />} />
                    <Route path="/clients" element={<ProtectedRoute component={<Clients />} allowedRoles={['admin', 'superadmin', 'rop', 'seller', 'employee', 'staff', 'partner']} />} />
                    <Route path="/inventory" element={<ProtectedRoute component={<Inventory />} allowedRoles={['admin', 'superadmin', 'partner']} />} />
                    <Route path="/profile" element={<ProtectedRoute component={<Profile />} />} />
                    <Route path="/crm" element={<ProtectedRoute component={<CRM />} allowedRoles={['admin', 'superadmin', 'rop', 'seller', 'employee', 'staff', 'partner']} />} />
                    <Route path="/bots" element={<ProtectedRoute component={<Bots />} allowedRoles={['admin', 'superadmin', 'partner']} />} />
                    <Route path="/events" element={<ProtectedRoute component={<Events />} allowedRoles={['admin', 'superadmin', 'rop', 'seller', 'employee', 'staff', 'partner']} />} />
                    <Route path="/events/:id/checkin" element={<ProtectedRoute component={<React.Suspense fallback={null}><Checkin /></React.Suspense>} allowedRoles={['admin', 'superadmin', 'rop', 'seller', 'employee', 'staff', 'partner']} />} />
                    <Route path="/referrals" element={<ProtectedRoute component={<Referrals />} allowedRoles={['admin', 'superadmin', 'partner']} />} />
                    <Route path="/analytics" element={<ProtectedRoute component={<Analytics />} allowedRoles={['admin', 'superadmin', 'rop', 'partner']} />} />
                    <Route path="/logs" element={<ProtectedRoute component={<Logs />} allowedRoles={['admin', 'superadmin']} />} />
                    <Route path="/members" element={<ProtectedRoute component={<Members />} allowedRoles={['admin', 'superadmin']} />} />
                    <Route path="/ai-settings" element={<ProtectedRoute component={<AISettings />} allowedRoles={['admin', 'superadmin']} />} />
                    <Route path="/settings" element={<ProtectedRoute component={<Settings />} allowedRoles={['admin', 'superadmin']} />} />
                    
                    <Route path="/calculator" element={<Calculator />} />
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </motion.div>
        </AnimatePresence>
    );
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <Toaster position="top-right" />
            <AuthProvider>
                <SettingsProvider>
                    <Router>
                        <React.Suspense fallback={
                            <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
                                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        }>
                            <AnimatedRoutes />
                        </React.Suspense>
                    </Router>
                </SettingsProvider>
            </AuthProvider>
        </QueryClientProvider>
    )
}

export default App
